import { useRef, useCallback, useEffect } from 'react'

interface UseRenderSchedulerOptions {
	isMobile?: boolean
	debounceMs?: number
}

export const useRenderScheduler = (options: UseRenderSchedulerOptions = {}) => {
	const { isMobile = false, debounceMs = 400 } = options

	const rafRef = useRef<number | null>(null)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const pendingRenderRef = useRef<(() => void) | null>(null)

	const scheduleRender = useCallback(
		(renderFn: () => void) => {
			if (rafRef.current != null) return

			rafRef.current = requestAnimationFrame(() => {
				rafRef.current = null

				if (isMobile) {
					if (timeoutRef.current) {
						clearTimeout(timeoutRef.current)
					}
					pendingRenderRef.current = renderFn
					timeoutRef.current = setTimeout(() => {
						if (pendingRenderRef.current) {
							pendingRenderRef.current()
							pendingRenderRef.current = null
						}
						timeoutRef.current = null
					}, debounceMs)
				} else {
					renderFn()
				}
			})
		},
		[isMobile, debounceMs]
	)

	const cancelScheduled = useCallback(() => {
		if (rafRef.current != null) {
			cancelAnimationFrame(rafRef.current)
			rafRef.current = null
		}
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
		pendingRenderRef.current = null
	}, [])

	useEffect(() => {
		return () => {
			cancelScheduled()
		}
	}, [cancelScheduled])

	return { scheduleRender, cancelScheduled }
}
